import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {Link, useParams} from 'react-router-dom';
import StatementSummaryForm from "./NewRevolverStatementSummary";
import GroupedBarChart from "./GroupedCreditCardBarChart";
import {formatCurrency, formatRate} from "./CoreFunctions";
import { exportToExcel } from './utils/exportUtils';
import Modal from './components/Modal';


const HelocDetail = () => {
    const { id } = useParams();  // Get credit card ID from the URL
    const [chartData, setChartData] = useState(null); // will hold the array of objects
    const [statementSummaryData, setStatementSummaryData] = useState([]);
    const [transactionData, setTransactionData] = useState([]);
    const [heloc, setHeloc] = useState([]);
    const [loading, setLoading] = useState(true); // Manage loading state
    const [viewMode, setViewMode] = useState('statements'); // 'statements' or 'transactions'
    const [activeRange, setActiveRange] = useState('12months'); // Default to last 12 months for transactions
    
    // Sorting state for the Statement Summaries table
    const [sortConfig, setSortConfig] = useState({ key: 'period', direction: 'desc' }); // default newest → oldest
    // Sorting state for the Transactions table
    const [txnSortConfig, setTxnSortConfig] = useState({ key: 'transaction_date', direction: 'desc' });

    // New transaction modal state
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingTxnId, setEditingTxnId] = useState(null);
    const [newTransaction, setNewTransaction] = useState({
        transaction_date: new Date().toISOString().split('T')[0],
        post_date: new Date().toISOString().split('T')[0],
        description: '',
        amount: '',
        expense_category: ''
    });
    const [addingTransaction, setAddingTransaction] = useState(false);
    const [openDropdownId, setOpenDropdownId] = useState(null);

    const HELOC_CATEGORIES = [
        { value: "payments", label: "Debt Payment" },
        { value: "debt_disbursement", label: "Debt Disbursement" },
        { value: "fees_charges", label: "Fees & Charges" }
    ];


    // useEffect(() => {
    //     // Fetch the heloc data from the API
    //     axios.get(`/api/balance_sheet/financing/revolvers/helocs/${id}/`)
    //         .then(response => {
    //             console.log(response.data); // Log the response data for debugging
    //             setHeloc(response.data); // Populate the component state
    //             setLoading(false); // Stop loading
    //         })
    //         .catch(error => {
    //             console.error('Error fetching heloc data:', error);
    //             setLoading(false); // Stop loading even on error
    //         });
    // }, []); // Empty dependency array ensures this runs only on mount
    //
    // // Loading message while fetching data
    // if (loading) {
    //     return <div>Loading HELOC...</div>;
    // }

    const ExcelIcon = () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
    );

    useEffect(() => {
        const handleClickOutside = () => {
            setOpenDropdownId(null);
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const handleExport = () => {
        const columns = [
            { header: 'Period', key: item => {
                if (item.statement_month_int && item.statement_year_int) {
                    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                    return `${monthNames[item.statement_month_int - 1]} ${item.statement_year_int}`;
                }
                return `${item.billing_cycle_start_date || ''} - ${item.billing_cycle_end_date || ''}`;
            }},
            { header: 'Previous Balance', key: 'previous_balance' },
            { header: 'Transactions', key: 'transactions' },
            { header: 'Interest Charged', key: 'interest_charged' },
            { header: 'Payments', key: item => (Number(item.payments)||0) + (Number(item.other_credits)||0) },
            { header: 'New Balance', key: 'new_balance' }
        ];
        
        const helocName = heloc?.account_name || heloc?.lender_name || heloc?.lender || id || 'HELOC';
        const lastFour = heloc?.account_number ? heloc.account_number.slice(-4) : '';
        const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '_');
        const filename = `HELOC_Statement_Summaries_${helocName}${lastFour ? '_' + lastFour : ''}_${dateStr}`;
        exportToExcel(statementSummaryData, filename, columns);
    };

    const refreshStatementSummaries = async () => {
        try {
            const res = await axios.get(`/api/balance_sheet/financing/revolvers/${id}/statement-summary-data/`);
            const data = res.data;
            if (!Array.isArray(data)) {
                console.warn('Expected array for statement summary data, got:', data);
                setStatementSummaryData([]);
                setChartData([]);
                return;
            }
            // Save raw items for table rendering
            setStatementSummaryData(data);
            const grouped = {};
            data.forEach(item => {
              const year = item.statement_year_int;
              const month = item.statement_month_int;
              const key = `${year}-${month}`;
              if (!grouped[key]) {
                const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                const label = `${monthNames[month - 1]} ${year}`;
                grouped[key] = { label, year, month, previous_balance:0, transactions:0, interest_charged:0, payments:0, balance_subject_to_interest_rate:0, new_balance:0 };
              }
              grouped[key].previous_balance += Number(item.previous_balance) || 0;
              grouped[key].transactions += Number(item.transactions) || 0;
              grouped[key].interest_charged += Number(item.interest_charged) || 0;
              const payPlusCred = (Number(item.payments) || 0) + (Number(item.other_credits) || 0);
              grouped[key].payments += payPlusCred;
              grouped[key].balance_subject_to_interest_rate += Number(item.balance_subject_to_interest_rate) || 0;
              grouped[key].new_balance += Number(item.new_balance) || 0;
            });
            const sortedKeys = Object.keys(grouped).sort((a,b)=> grouped[a].year - grouped[b].year || grouped[a].month - grouped[b].month);
            const chartDataArray = sortedKeys.map(key => ({
              period: grouped[key].label,
              previous_balance: grouped[key].previous_balance,
              transactions: grouped[key].transactions,
              interest_charged: grouped[key].interest_charged,
              payments: grouped[key].payments,
              balance_subject_to_interest_rate: grouped[key].balance_subject_to_interest_rate,
              new_balance: grouped[key].new_balance,
            }));
            setChartData(chartDataArray);
        } catch (err) {
            console.error('Error fetching and processing data:', err);
        }
    };

    const refreshTransactions = async (range = activeRange) => {
        try {
            let url = `/api/balance_sheet/financing/revolvers/transactions/by-revolver/${id}/`;
            
            if (range !== 'all') {
                const now = new Date();
                let startDate = new Date();
                if (range === '12months') {
                    startDate.setMonth(now.getMonth() - 11);
                    startDate.setDate(1);
                } else if (range === '3months') {
                    startDate.setMonth(now.getMonth() - 2);
                    startDate.setDate(1);
                } else if (range === 'YTD') {
                    startDate = new Date(now.getFullYear(), 0, 1);
                }
                const startDateStr = startDate.toISOString().split('T')[0];
                url += `?start_date=${startDateStr}`;
            }

            const res = await axios.get(url);
            const list = Array.isArray(res.data) ? res.data : (res.data.transactions || []);
            setTransactionData(list);
        } catch (err) {
            console.error('Error fetching transactions:', err);
        }
    };

    const handleRangeChange = (range) => {
        setActiveRange(range);
        refreshTransactions(range);
    };

    const handleAddTransaction = async () => {
        if (!newTransaction.transaction_date || !newTransaction.description || !newTransaction.amount) {
            alert("Please fill in required fields.");
            return;
        }
        
        const today = new Date().toISOString().split('T')[0];
        if (newTransaction.transaction_date > today) {
            alert("Transaction date cannot be in the future.");
            return;
        }

        setAddingTransaction(true);
        try {
            const payload = {
                revolver: id,
                ...newTransaction,
                amount: parseFloat(newTransaction.amount),
                source: 'user_supplied'
            };
            if (editingTxnId) {
                await axios.patch(`/api/balance_sheet/financing/revolvers/transactions/${editingTxnId}/`, payload);
            } else {
                await axios.post('/api/balance_sheet/financing/revolvers/transactions/', payload);
            }
            setShowAddModal(false);
            setEditingTxnId(null);
            setNewTransaction({
                transaction_date: new Date().toISOString().split('T')[0],
                post_date: new Date().toISOString().split('T')[0],
                description: '',
                amount: '',
                expense_category: ''
            });
            refreshTransactions();
        } catch (err) {
            console.error('Error adding/updating transaction:', err);
            alert(`Failed to ${editingTxnId ? 'update' : 'add'} transaction.`);
        } finally {
            setAddingTransaction(false);
        }
    };

    const handleEditClick = (txn) => {
        setEditingTxnId(txn.id);
        setNewTransaction({
            transaction_date: txn.transaction_date,
            post_date: txn.post_date || txn.transaction_date,
            description: txn.description,
            amount: txn.amount,
            expense_category: txn.expense_category || ''
        });
        setShowAddModal(true);
    };

    const handleDeleteTransaction = async (txnId) => {
        if (!window.confirm("Are you sure you want to delete this transaction?")) return;
        try {
            await axios.delete(`/api/balance_sheet/financing/revolvers/transactions/${txnId}/`);
            refreshTransactions();
        } catch (err) {
            console.error('Error deleting transaction:', err);
            alert("Failed to delete transaction.");
        }
    };

    const handleCategoryChange = async (txnId, newCategory) => {
        try {
            await axios.patch(`/api/balance_sheet/financing/revolvers/transactions/${txnId}/`, {
                expense_category: newCategory
            });
            refreshTransactions();
        } catch (err) {
            console.error('Error updating transaction category:', err);
            alert("Failed to update category.");
        }
    };

    const toggleDropdown = (e, transactionId) => {
        e.stopPropagation();
        setOpenDropdownId(openDropdownId === transactionId ? null : transactionId);
    };

    const getCategoryStyle = (category) => {
        const styles = {
            'fees_charges': { backgroundColor: '#FFEBEE', color: '#D32F2F', borderColor: '#D32F2F' },
            'payments': { backgroundColor: '#E8F5E8', color: '#2E7D32', borderColor: '#2E7D32' },
            'debt_disbursement': { backgroundColor: '#F3E5F5', color: '#7B1FA2', borderColor: '#7B1FA2' }, // Purple-ish like gas/fuel or similar
            'other': { backgroundColor: '#F5F5F5', color: '#616161', borderColor: '#616161' },
            'none': { backgroundColor: '#FFFFFF', color: '#000000', borderColor: '#000000' },
        };
        return styles[category] || styles['none'];
    };

    const renderExpenseCategory = (transaction) => {
        const category = transaction.expense_category || 'none';
        const label = HELOC_CATEGORIES.find(c => c.value === category)?.label || 'None';
        const style = getCategoryStyle(category);
        const isOpen = openDropdownId === transaction.id;

        return (
            <div className="position-relative" style={{ display: 'inline-block' }}>
                <button
                    className="btn btn-sm"
                    type="button"
                    onClick={(e) => toggleDropdown(e, transaction.id)}
                    style={{
                        backgroundColor: style.backgroundColor,
                        color: style.color,
                        borderColor: style.borderColor,
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        padding: '4px 8px',
                        border: `1px solid ${style.borderColor}`,
                        cursor: 'pointer',
                        whiteSpace:'nowrap'
                    }}
                >
                    {label} ▼
                </button>

                {isOpen && (
                    <div
                        className="position-absolute"
                        style={{
                            top: '100%',
                            left: '0',
                            zIndex: 1000,
                            minWidth: '200px',
                            backgroundColor: '#111827',
                            border: '1px solid #374151',
                            borderRadius: '0.375rem',
                            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.3)',
                            marginTop: '2px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ padding: '4px 0' }}>
                            {HELOC_CATEGORIES.map((cat) => (
                                <button
                                    key={cat.value}
                                    className="w-100 text-start"
                                    style={{
                                        width: '100%',
                                        display: 'block',
                                        padding: '8px 16px',
                                        border: 'none',
                                        backgroundColor: '#111827',
                                        cursor: 'pointer',
                                        fontSize: '0.875rem',
                                        color: '#e5e7eb'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#111827'}
                                    onClick={() => {
                                        handleCategoryChange(transaction.id, cat.value);
                                        setOpenDropdownId(null);
                                    }}
                                >
                                    {cat.label}
                                </button>
                            ))}
                            <button
                                className="w-100 text-start"
                                style={{
                                    width: '100%',
                                    display: 'block',
                                    padding: '8px 16px',
                                    border: 'none',
                                    backgroundColor: '#111827',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    color: '#9ca3af',
                                    borderTop: '1px solid #374151'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#111827'}
                                onClick={() => {
                                    handleCategoryChange(transaction.id, '');
                                    setOpenDropdownId(null);
                                }}
                            >
                                None
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    useEffect(() => {
        axios.get(`/api/balance_sheet/financing/revolvers/helocs/${id}/`)
            .then(response => {
                console.log(response.data); // Log the response data for debugging
                setHeloc(response.data); // Populate the component state
                setLoading(false); // Stop loading
            })
            .catch(error => {
                console.error('Error fetching heloc data:', error);
                setLoading(false); // Stop loading even on error
            });
        refreshStatementSummaries();
        refreshTransactions();
      }, []);

    // Remove action temporarily disabled per request

    return (
        <div style={{ marginLeft: '40px' }}>
            {/* Header with title (linked property) and Add button on right */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginRight: '50px' }}>
                <h1 style={{ margin: 0 }}>
                    HELOC on{' '}
                    {heloc?.asset_info?.id ? (
                        <Link to={`/balance_sheet/assets/physical_assets/resi_properties/${heloc.asset_info.id}`}>
                            {heloc.asset_info.description || `Property ${heloc.asset_info.id}`}
                        </Link>
                    ) : (
                        heloc?.property || 'Property'
                    )}
                </h1>
            </div>

            {/* Container for Stats and Graph */}
            <div style={{ display: 'flex', gap: '20px', marginLeft: '10px', marginRight: '50px' }}>
                <div className="top-section">
                    <div className="loan-stats">
                        <p><strong>HELOC Details: </strong></p>
                        {heloc ? (
                            <>
                                <p style={{ marginLeft: '30px' }}>Lender: {heloc.lender_name}</p>
                                <p style={{ marginLeft: '30px' }}>Term (Years): {heloc.term_in_years}</p>
                                <p style={{ marginLeft: '30px' }}>Origination Date: {heloc.origination_date}</p>
                                <p style={{ marginLeft: '30px' }}>Line Amount: {formatCurrency(heloc.current_credit_limit)}</p>
                                <p style={{ marginLeft: '30px' }}>Current Rate: {formatRate(heloc.current_rate, 2)}</p>
                                <p style={{ marginLeft: '30px' }}>Maturity Date: {heloc.maturity_date || 'n/a'}</p>
                            </>
                        ) : (
                            <p style={{ marginLeft: '30px' }}>No upcoming payment available.</p>
                        )}
                        <p><strong>Stats: </strong></p>
                        {heloc.outstanding_balance ? (
                            <p style={{ marginLeft: '30px' }}>
                                Outstanding: {heloc.outstanding_balance} ({heloc.percent_outstanding})
                            </p>
                        ) : (
                            <p style={{ marginLeft: '30px' }}>No recent payment data available.</p>
                        )}
                    </div>
                </div>

                <div style={{ flex: 1, marginTop: '40px', marginLeft: '20px', marginRight: '30px'}}>
                  {chartData ? <GroupedBarChart data={chartData} /> : <p>No data available</p>}
                </div>
            </div>
            {/* Removed the loan summary table and Remove button as requested */}

            {/* Toggle and Tables Section */}
            <div style={{ marginLeft: '40px', marginRight: '50px', marginBottom: '20px', marginTop: '40px' }}>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                    <button 
                        className={`btn ${viewMode === 'statements' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('statements')}
                    >
                        Statement Summaries
                    </button>
                    <button 
                        className={`btn ${viewMode === 'transactions' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('transactions')}
                    >
                        Transactions
                    </button>
                </div>

                {viewMode === 'statements' ? (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                            <h3 style={{ margin: 0 }}>Statement Summaries</h3>
                            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <StatementSummaryForm revolver={heloc} onSuccess={refreshStatementSummaries} />
                                <button 
                                    onClick={handleExport} 
                                    className="btn btn-outline-success btn-sm"
                                    title="Export to Excel"
                                >
                                    <ExcelIcon /> Export
                                </button>
                            </div>
                        </div>
                        <table className="heloc-table">
                            <thead>
                                <tr>
                                    <th
                                      onClick={() => setSortConfig(prev => ({ key: 'period', direction: prev.key==='period' && prev.direction==='desc' ? 'asc' : 'desc' }))}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      Period {sortConfig.key==='period' && (sortConfig.direction==='asc' ? '▲' : '▼')}
                                    </th>
                                    <th
                                      onClick={() => setSortConfig(prev => ({ key: 'previous_balance', direction: prev.key==='previous_balance' && prev.direction==='desc' ? 'asc' : 'desc' }))}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      Prev Balance {sortConfig.key==='previous_balance' && (sortConfig.direction==='asc' ? '▲' : '▼')}
                                    </th>
                                    <th
                                      onClick={() => setSortConfig(prev => ({ key: 'transactions', direction: prev.key==='transactions' && prev.direction==='desc' ? 'asc' : 'desc' }))}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      Transactions {sortConfig.key==='transactions' && (sortConfig.direction==='asc' ? '▲' : '▼')}
                                    </th>
                                    <th
                                      onClick={() => setSortConfig(prev => ({ key: 'interest_charged', direction: prev.key==='interest_charged' && prev.direction==='desc' ? 'asc' : 'desc' }))}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      Interest {sortConfig.key==='interest_charged' && (sortConfig.direction==='asc' ? '▲' : '▼')}
                                    </th>
                                    <th
                                      onClick={() => setSortConfig(prev => ({ key: 'payments', direction: prev.key==='payments' && prev.direction==='desc' ? 'asc' : 'desc' }))}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      Payments {sortConfig.key==='payments' && (sortConfig.direction==='asc' ? '▲' : '▼')}
                                    </th>
                                    <th
                                      onClick={() => setSortConfig(prev => ({ key: 'new_balance', direction: prev.key==='new_balance' && prev.direction==='desc' ? 'asc' : 'desc' }))}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      New Balance {sortConfig.key==='new_balance' && (sortConfig.direction==='asc' ? '▲' : '▼')}
                                    </th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    const getSortVal = (item, key) => {
                                      switch(key) {
                                        case 'period': {
                                          const y = Number(item.statement_year_int) || 0;
                                          const m = Number(item.statement_month_int) || 0;
                                          if (y && m) return y * 100 + m;
                                          const end = item.billing_cycle_end_date ? Date.parse(item.billing_cycle_end_date) : 0;
                                          return end || 0;
                                        }
                                        case 'previous_balance': return Number(item.previous_balance) || 0;
                                        case 'transactions': return Number(item.transactions) || 0;
                                        case 'interest_charged': return Number(item.interest_charged) || 0;
                                        case 'payments': return (Number(item.payments)||0) + (Number(item.other_credits)||0);
                                        case 'new_balance': return Number(item.new_balance) || 0;
                                        default: return 0;
                                      }
                                    };
                                    const sorted = Array.isArray(statementSummaryData) ? [...statementSummaryData].sort((a,b)=>{
                                      const va = getSortVal(a, sortConfig.key);
                                      const vb = getSortVal(b, sortConfig.key);
                                      if (va < vb) return sortConfig.direction === 'asc' ? -1 : 1;
                                      if (va > vb) return sortConfig.direction === 'asc' ? 1 : -1;
                                      return 0;
                                    }) : [];
                                    return sorted.map(item => {
                                    const periodLabel = (() => {
                                        if (item.statement_month_int && item.statement_year_int) {
                                            const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                                            return `${monthNames[item.statement_month_int - 1]} ${item.statement_year_int}`;
                                        }
                                        if (item.billing_cycle_start_date || item.billing_cycle_end_date) {
                                            return `${item.billing_cycle_start_date || ''} - ${item.billing_cycle_end_date || ''}`;
                                        }
                                        return 'N/A';
                                    })();
                                    const paymentsTotal = (Number(item.payments)||0) + (Number(item.other_credits)||0);
                                    return (
                                        <tr key={item.id}>
                                            <td>{periodLabel}</td>
                                            <td>{formatCurrency(item.previous_balance)}</td>
                                            <td>{formatCurrency(item.transactions)}</td>
                                            <td>{formatCurrency(item.interest_charged)}</td>
                                            <td>{formatCurrency(paymentsTotal)}</td>
                                            <td>{formatCurrency(item.new_balance)}</td>
                                            <td>
                                                <StatementSummaryForm
                                                    revolver={heloc}
                                                    onSuccess={refreshStatementSummaries}
                                                    triggerLabel="Edit"
                                                    triggerClassName="btn btn-primary btn-sm"
                                                    initialData={item}
                                                    recordId={item.id}
                                                />
                                            </td>
                                        </tr>
                                    )
                                    });
                                })()}
                            </tbody>
                        </table>
                    </>
                ) : (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                            <h3 style={{ margin: 0 }}>Transactions</h3>
                            <div style={{ marginLeft: '20px' }}>
                                <button 
                                    onClick={() => handleRangeChange('12months')}
                                    className={`btn btn-sm ${activeRange === '12months' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    style={{ marginRight: '5px' }}
                                >
                                    Last 12 Months
                                </button>
                                <button 
                                    onClick={() => handleRangeChange('3months')}
                                    className={`btn btn-sm ${activeRange === '3months' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    style={{ marginRight: '5px' }}
                                >
                                    Last 3 Months
                                </button>
                                <button 
                                    onClick={() => handleRangeChange('YTD')}
                                    className={`btn btn-sm ${activeRange === 'YTD' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    style={{ marginRight: '5px' }}
                                >
                                    YTD
                                </button>
                                <button 
                                    onClick={() => handleRangeChange('all')}
                                    className={`btn btn-sm ${activeRange === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                                >
                                    Show All
                                </button>
                            </div>
                            <div style={{ marginLeft: 'auto' }}>
                                <button 
                                    onClick={() => setShowAddModal(true)} 
                                    className="btn btn-success"
                                >
                                    + Transactions
                                </button>
                            </div>
                        </div>
                        <table className="heloc-table">
                            <thead>
                                <tr>
                                    <th 
                                        onClick={() => setTxnSortConfig(prev => ({ key: 'transaction_date', direction: prev.key==='transaction_date' && prev.direction==='desc' ? 'asc' : 'desc' }))}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        Date {txnSortConfig.key==='transaction_date' && (txnSortConfig.direction==='asc' ? '▲' : '▼')}
                                    </th>
                                    <th
                                        onClick={() => setTxnSortConfig(prev => ({ key: 'description', direction: prev.key==='description' && prev.direction==='desc' ? 'asc' : 'desc' }))}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        Description {txnSortConfig.key==='description' && (txnSortConfig.direction==='asc' ? '▲' : '▼')}
                                    </th>
                                    <th
                                        onClick={() => setTxnSortConfig(prev => ({ key: 'amount', direction: prev.key==='amount' && prev.direction==='desc' ? 'asc' : 'desc' }))}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        Amount {txnSortConfig.key==='amount' && (txnSortConfig.direction==='asc' ? '▲' : '▼')}
                                    </th>
                                    <th>Category</th>
                                    <th>Linked To-Do</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(!transactionData || transactionData.length === 0) && (
                                    <tr><td colSpan="6" style={{ textAlign: 'center' }}>No transactions found.</td></tr>
                                )}
                                {(Array.isArray(transactionData) ? [...transactionData] : []).sort((a,b) => {
                                    const va = a[txnSortConfig.key];
                                    const vb = b[txnSortConfig.key];
                                    if (va < vb) return txnSortConfig.direction === 'asc' ? -1 : 1;
                                    if (va > vb) return txnSortConfig.direction === 'asc' ? 1 : -1;
                                    return 0;
                                }).map(item => (
                                    <tr key={item.id}>
                                        <td>{item.transaction_date}</td>
                                        <td>{item.description}</td>
                                        <td>{formatCurrency(item.amount)}</td>
                                        <td>
                                            {renderExpenseCategory(item)}
                                        </td>
                                        <td>
                                            {(() => {
                                                const todoInfo = item.linked_todo_info;
                                                if (!todoInfo) return null;

                                                let url = null;
                                                if (todoInfo.asset_type === 'vehicle') {
                                                    url = `/balance_sheet/assets/physical_assets/vehicles/${todoInfo.asset_id}?tab=todo`;
                                                } else if (todoInfo.property_id) {
                                                    url = `/balance_sheet/assets/physical_assets/residential/${todoInfo.property_id}?tab=todo`;
                                                }

                                                const statusColor = todoInfo.status === 'completed' ? '#22c55e' : (todoInfo.status === 'in_progress' ? '#3b82f6' : '#9ca3af');
                                                
                                                const content = (
                                                    <span 
                                                        style={{ color: '#93c5fd', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                        title={`To-Do: ${todoInfo.title}\nStatus: ${todoInfo.status_display}`}
                                                    >
                                                        {todoInfo.title}
                                                    </span>
                                                );

                                                return (
                                                    <span style={{ display:'inline-flex', gap:4, alignItems:'center' }}>
                                                        {url ? (
                                                            <Link to={url} style={{ textDecoration:'none' }}>{content}</Link>
                                                        ) : (
                                                            content
                                                        )}
                                                        <span style={{ fontSize: '10px', color: statusColor, border: `1px solid ${statusColor}`, borderRadius: '4px', padding: '0 4px' }}>
                                                            {todoInfo.status_display}
                                                        </span>
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '5px' }}>
                                                <button 
                                                    className="btn btn-primary btn-sm"
                                                    style={{ marginRight: '5px' }}
                                                    onClick={() => handleEditClick(item)}
                                                >
                                                    Edit
                                                </button>
                                                <button 
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => handleDeleteTransaction(item.id)}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {transactionData.length === 0 && (
                                    <tr>
                                        <td colSpan="5" style={{ textAlign: 'center' }}>No transactions found.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </>
                )}
            </div>

            <Modal 
                isOpen={showAddModal} 
                onClose={() => {
                    setShowAddModal(false);
                    setEditingTxnId(null);
                }}
                title={editingTxnId ? "Edit HELOC Transaction" : "Add HELOC Transaction"}
                width={500}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <div>
                        <label className="form-label">Date</label>
                        <input 
                            type="date" 
                            className="form-control"
                            value={newTransaction.transaction_date}
                            max={new Date().toISOString().split('T')[0]}
                            onChange={(e) => setNewTransaction({...newTransaction, transaction_date: e.target.value, post_date: e.target.value})}
                        />
                    </div>
                    <div>
                        <label className="form-label">Description</label>
                        <input 
                            type="text" 
                            className="form-control"
                            value={newTransaction.description}
                            onChange={(e) => setNewTransaction({...newTransaction, description: e.target.value})}
                        />
                    </div>
                    <div>
                        <label className="form-label">Amount</label>
                        <input 
                            type="number" 
                            step="0.01"
                            className="form-control"
                            value={newTransaction.amount}
                            onChange={(e) => setNewTransaction({...newTransaction, amount: e.target.value})}
                        />
                        <div className="form-text text-muted" style={{ fontSize: '0.85rem', marginTop: '4px' }}>
                            Use <strong>positive</strong> for charges and <strong>negative</strong> for payments.
                        </div>
                    </div>
                    <div>
                        <label className="form-label">Category (Optional)</label>
                        <select 
                            className="form-select"
                            value={newTransaction.expense_category}
                            onChange={(e) => setNewTransaction({...newTransaction, expense_category: e.target.value})}
                        >
                            <option value="">-- Select Category --</option>
                            {HELOC_CATEGORIES.map(cat => (
                                <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                        </select>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                        <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                        <button className="btn btn-primary" onClick={handleAddTransaction} disabled={addingTransaction}>
                            {addingTransaction ? (editingTxnId ? 'Updating...' : 'Adding...') : (editingTxnId ? 'Update Transaction' : 'Add Transaction')}
                        </button>
                    </div>
                </div>
            </Modal>
            <style jsx>{`
                .heloc-table {
                    margin: 20px;
                    width: 90%;
                    table-layout: auto;
                    border-collapse: collapse;
                }
                .heloc-table th, .heloc-table td {
                    border: 1px solid #ccc;
                    padding: 10px;
                    text-align: left;
                }
                .heloc-table th {
                    background-color: #f0f0f0;
                }
                .btn.btn-primary.btn-sm {
                    background-color: #007bff;
                }
                .heloc-table th { user-select: none; }
            `}</style>
        </div>
    );
};

export default HelocDetail;